import { useState, useEffect, useCallback } from 'react';
import { useMeeting } from '@videosdk.live/react-sdk';

const useAudioQuality = () => {
  const [audioUseCase, setAudioUseCase] = useState('meeting'); // 'meeting', 'music', 'highQuality', 'lowBandwidth'
  const [participantCount, setParticipantCount] = useState(0);
  const [noiseSuppressionEnabled, setNoiseSuppressionEnabled] = useState(true);
  const [echoCancellationEnabled, setEchoCancellationEnabled] = useState(true);
  const [autoGainControlEnabled, setAutoGainControlEnabled] = useState(true);
  const [autoOptimizationEnabled, setAutoOptimizationEnabled] = useState(true);
  const [audioLevel, setAudioLevel] = useState(0);
  const [backgroundNoiseLevel, setBackgroundNoiseLevel] = useState('low'); // 'low', 'medium', 'high'

  const meeting = useMeeting();
  const participants = meeting?.participants;
  const localMicOn = meeting?.localMicOn;

  // Monitor participant count changes
  useEffect(() => {
    if (participants) {
      const count = new Map(participants).size + 1;
      setParticipantCount(count);
    }
  }, [participants]);

  // Detect background noise level (simplified simulation)
  useEffect(() => {
    const detectBackgroundNoise = () => {
      // In a real implementation, you'd analyze audio input levels
      // For now, we'll simulate based on device type and participant count
      const isMobile = window.matchMedia("(max-width: 768px)").matches;
      
      if (isMobile && participantCount > 4) {
        setBackgroundNoiseLevel('high');
      } else if (participantCount > 8) {
        setBackgroundNoiseLevel('medium');
      } else {
        setBackgroundNoiseLevel('low');
      }
    };

    detectBackgroundNoise();
  }, [participantCount]);

  // Get recommended audio configuration
  const getRecommendedAudioConfig = useCallback(() => {
    if (!autoOptimizationEnabled) return null;

    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const networkQuality = connection?.effectiveType === '4g' ? 'high' : 'medium';

    let recommendedConfig = {
      encoderConfig: "speech_standard",
      noiseConfig: {
        noiseSuppression: true,
        echoCancellation: true,
        autoGainControl: true
      }
    };

    // Auto-adjust based on use case
    switch (audioUseCase) {
      case 'music':
        recommendedConfig.encoderConfig = "music_standard";
        recommendedConfig.noiseConfig.noiseSuppression = false;
        recommendedConfig.noiseConfig.autoGainControl = false;
        break;
        
      case 'highQuality':
        recommendedConfig.encoderConfig = "high_quality";
        break;
        
      case 'lowBandwidth':
        recommendedConfig.encoderConfig = "speech_standard";
        recommendedConfig.noiseConfig.noiseSuppression = true;
        break;
    }

    // Environmental adjustments
    if (backgroundNoiseLevel === 'high') {
      recommendedConfig.noiseConfig.noiseSuppression = true;
      recommendedConfig.noiseConfig.autoGainControl = true;
    }

    if (participantCount > 8) {
      recommendedConfig.noiseConfig.noiseSuppression = true;
    }

    if (isMobile) {
      recommendedConfig.noiseConfig.noiseSuppression = true;
      recommendedConfig.noiseConfig.autoGainControl = true;
    }

    return recommendedConfig;
  }, [audioUseCase, backgroundNoiseLevel, participantCount, autoOptimizationEnabled]);

  // Switch audio quality method
  const switchAudioQuality = useCallback(async (useCase) => {
    if (!meeting) return;

    try {
      const { createMicrophoneAudioTrack } = await import('@videosdk.live/react-sdk');
      
      setAudioUseCase(useCase);
      
      const config = getRecommendedAudioConfig();
      
      if (config && meeting.localMicOn) {
        // Mute first to avoid audio issues
        meeting.muteMic();
        
        // Small delay to ensure clean transition
        setTimeout(async () => {
          const customTrack = await createMicrophoneAudioTrack({
            encoderConfig: config.encoderConfig,
            noiseConfig: {
              noiseSuppression: noiseSuppressionEnabled,
              echoCancellation: echoCancellationEnabled,
              autoGainControl: autoGainControlEnabled
            }
          });
          
          meeting.unmuteMic(customTrack);
        }, 100);
      }
    } catch (error) {
      console.error('Error switching audio quality:', error);
    }
  }, [meeting, getRecommendedAudioConfig, noiseSuppressionEnabled, echoCancellationEnabled, autoGainControlEnabled]);

  // Toggle individual audio features
  const toggleNoiseSuppression = useCallback(async () => {
    setNoiseSuppressionEnabled(prev => {
      const newValue = !prev;
      if (autoOptimizationEnabled && meeting?.localMicOn) {
        switchAudioQuality(audioUseCase);
      }
      return newValue;
    });
  }, [autoOptimizationEnabled, meeting, switchAudioQuality, audioUseCase]);

  const toggleEchoCancellation = useCallback(async () => {
    setEchoCancellationEnabled(prev => {
      const newValue = !prev;
      if (autoOptimizationEnabled && meeting?.localMicOn) {
        switchAudioQuality(audioUseCase);
      }
      return newValue;
    });
  }, [autoOptimizationEnabled, meeting, switchAudioQuality, audioUseCase]);

  const toggleAutoGainControl = useCallback(async () => {
    setAutoGainControlEnabled(prev => {
      const newValue = !prev;
      if (autoOptimizationEnabled && meeting?.localMicOn) {
        switchAudioQuality(audioUseCase);
      }
      return newValue;
    });
  }, [autoOptimizationEnabled, meeting, switchAudioQuality, audioUseCase]);

  // Audio level monitoring (simplified)
  useEffect(() => {
    let intervalId;
    
    if (localMicOn) {
      intervalId = setInterval(() => {
        // Simulate audio level detection
        // In real implementation, you'd analyze the actual audio stream
        const simulatedLevel = Math.random() * 100;
        setAudioLevel(simulatedLevel);
      }, 500);
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [localMicOn]);

  // Auto-optimization based on conditions
  useEffect(() => {
    if (autoOptimizationEnabled && meeting?.localMicOn) {
      const config = getRecommendedAudioConfig();
      if (config) {
        // Auto-adjust settings based on recommendations
        const shouldUpdateNoise = config.noiseConfig.noiseSuppression !== noiseSuppressionEnabled;
        const shouldUpdateEcho = config.noiseConfig.echoCancellation !== echoCancellationEnabled;
        const shouldUpdateGain = config.noiseConfig.autoGainControl !== autoGainControlEnabled;
        
        if (shouldUpdateNoise || shouldUpdateEcho || shouldUpdateGain) {
          setNoiseSuppressionEnabled(config.noiseConfig.noiseSuppression);
          setEchoCancellationEnabled(config.noiseConfig.echoCancellation);
          setAutoGainControlEnabled(config.noiseConfig.autoGainControl);
        }
      }
    }
  }, [backgroundNoiseLevel, participantCount, autoOptimizationEnabled, meeting?.localMicOn, getRecommendedAudioConfig, noiseSuppressionEnabled, echoCancellationEnabled, autoGainControlEnabled]);

  return {
    // State
    audioUseCase,
    participantCount,
    noiseSuppressionEnabled,
    echoCancellationEnabled,
    autoGainControlEnabled,
    autoOptimizationEnabled,
    audioLevel,
    backgroundNoiseLevel,
    recommendedConfig: getRecommendedAudioConfig(),
    
    // Controls
    setAudioUseCase: switchAudioQuality,
    setAutoOptimizationEnabled,
    toggleNoiseSuppression,
    toggleEchoCancellation,
    toggleAutoGainControl,
    
    // Utilities
    isHighNoiseEnvironment: backgroundNoiseLevel === 'high',
    isLargeMeeting: participantCount > 8,
    isMusicMode: audioUseCase === 'music',
    isHighQualityMode: audioUseCase === 'highQuality'
  };
};

export default useAudioQuality;